import { Injectable, inject } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, take } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import {
  AppleProfileStoreActions,
  CategoryStoreSelectors,
  DeviceStoreActions,
  DeviceStoreSelectors,
  DevicesRouterStoreSelectors,
} from '@era/stores';

/**
 * Resolves the device detail route by triggering Apple device + profile load, then waiting for the
 * device to have category IDs in the store (from the parent route's locations/devices request)
 * before activating the route, so the detail page always has a device with room/location etc.
 */
@Injectable({ providedIn: 'root' })
export class AppleDeviceResolver {
  private readonly store = inject(Store);

  resolve(): Observable<boolean> {
    return this.store.select(DevicesRouterStoreSelectors.selectUdid).pipe(
      first(),
      switchMap((udid) => {
        if (!udid) {
          return of(false);
        }

        // Trigger loading of Apple device and profile data (parent route triggers locations/devices list).
        this.store.dispatch(DeviceStoreActions.DeviceResolverActions.appleDeviceRequested({ udid }));
        this.store.dispatch(AppleProfileStoreActions.AppleDeviceResolverActions.profilesRequested({ udid }));
        // Wait until the device in the store has category IDs (merged from basicDeviceInfoLoaded),
        // so the detail page can resolve category and applications.
        return this.store.select(DeviceStoreSelectors.selectDeviceByUdid(udid)).pipe(
          filter((device) => !!device && CategoryStoreSelectors.deviceHasCategoryId(device)),
          take(1),
          map(() => true),
          catchError(() => of(true))
        );
      })
    );
  }
}
